import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import DataTable, { downloadBlob } from "@/components/DataTable";
import CommercialStatusBadge from "@/components/CommercialStatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Download, Upload, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function QuotationList() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);
  const tableRef = useRef(0);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);

  const fetchData = React.useCallback(async ({ skip, limit, sort, search, filters }) => {
    const params = { skip, limit, sort, search };
    if (filters.status) params.status = filters.status;
    if (filters.customer_id) params.customer_id = filters.customer_id;
    const { data } = await api.get("/quotations", { params });
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  async function handleExport() {
    const token = localStorage.getItem("hispl_token");
    const res = await fetch(`${API}/quotations/export`, { headers: { Authorization: `Bearer ${token}` } });
    await downloadBlob(res, "quotations.xlsx");
  }

  async function downloadTemplate() {
    const token = localStorage.getItem("hispl_token");
    const res = await fetch(`${API}/quotations/import/template`, { headers: { Authorization: `Bearer ${token}` } });
    await downloadBlob(res, "quotation_import_template.xlsx");
  }

  async function handleImport(file) {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/quotations/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      if (data.imported > 0) {
        toast.success(`Imported ${data.imported} quotations`);
        setReloadTick((t) => t + 1);
      } else {
        toast.warning("No quotations imported — see errors below");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); }
  }

  const columns = [
    { key: "display_number", label: "Quotation #", sortable: true,
      render: (r) => <span className="font-mono text-[12px] font-semibold text-blue-900" data-testid={`quotation-number-${r.id}`}>
        {r.display_number || r.quotation_number}
      </span> },
    { key: "inquiry_number", label: "Inquiry #",
      render: (r) => <span className="font-mono text-xs text-slate-600">{r.inquiry_number || "—"}</span> },
    { key: "quotation_type", label: "Type",
      render: (r) => {
        const map = { M: ["Manufacturing", "bg-blue-50 text-blue-800 border-blue-200"],
                      L: ["Reconditioning", "bg-rose-50 text-rose-700 border-rose-200"],
                      B: ["Budgetary", "bg-violet-50 text-violet-700 border-violet-200"] };
        const [label, cls] = map[r.quotation_type] || ["—", "bg-slate-100 text-slate-600 border-slate-200"];
        return <span className={`text-[10px] uppercase font-semibold font-mono px-1.5 py-0.5 rounded border ${cls}`}>{label}</span>;
      } },
    { key: "quotation_date", label: "Date", sortable: true, className: "font-mono text-xs text-slate-600" },
    { key: "current_revision", label: "Rev",
      render: (r) => <span className="font-mono text-xs font-semibold">R{r.current_revision}</span> },
    { key: "customer_name", label: "Customer", className: "text-slate-800" },
    { key: "plant_name", label: "Plant", visibleByDefault: false, className: "text-slate-600 text-xs" },
    { key: "line_count", label: "Lines",
      render: (r) => <span className="font-mono text-xs">{r.line_count}</span> },
    { key: "total_value", label: "Value ₹",
      render: (r) => <span className="font-mono text-xs font-semibold">₹{(r.total_value || 0).toLocaleString("en-IN")}</span> },
    { key: "valid_until", label: "Valid Until", visibleByDefault: false,
      render: (r) => <span className="font-mono text-xs text-slate-600">{r.valid_until || "—"}</span> },
    { key: "status", label: "Status", sortable: true,
      render: (r) => <CommercialStatusBadge status={r.status} /> },
  ];

  return (
    <div data-testid="quotation-list-page" className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Commercial Workflow · Stage 2</div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>Quotations</h1>
          <p className="text-sm text-slate-500 mt-1">Priced offers to customers, with full revision history (rev A/B/C…).</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="qtn-import-btn" onClick={() => setImportOpen(true)}>
            <Upload className="w-3.5 h-3.5 mr-1" /> Import Legacy Excel
          </Button>
        </div>
      </div>

      <DataTable
        key={reloadTick}
        columns={columns} fetchData={fetchData} onExport={handleExport}
        filters={[
          { key: "status", label: "Status",
            options: ["Draft", "Approved", "Rejected", "Revised"].map((s) => ({ value: s, label: s })) },
          { key: "customer_id", label: "Customer",
            options: customers.map((c) => ({ value: c.id, label: c.name })) },
        ]}
        storageKey="quotations"
        onRowClick={(r) => nav(`/quotations/${r.id}`)}
        emptyMessage="No quotations yet. Create one from an inquiry."
      />

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="qtn-import-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Legacy Quotations from Excel</DialogTitle>
            <DialogDescription>
              Upload your existing quotations. Each row = one line item. Rows are grouped by "Quotation Number".
              Duplicates are skipped. Customer &amp; Plant must already exist in Master Data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" data-testid="qtn-import-template" onClick={downloadTemplate}>
                <FileDown className="w-3.5 h-3.5 mr-1" /> Download template
              </Button>
              <span className="text-[11px] text-slate-500">Fill it, then choose the file below.</span>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              data-testid="qtn-import-file"
              onChange={(e) => handleImport(e.target.files?.[0])}
              className="text-xs w-full border border-slate-300 rounded px-2 py-1.5"
            />
            {importing && (
              <div className="text-xs text-slate-600 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Importing…
              </div>
            )}
            {importResult && (
              <div className="border border-slate-200 rounded p-3 text-xs space-y-2" data-testid="qtn-import-result">
                <div className="font-semibold text-slate-800">
                  Imported: <span className="text-emerald-700 font-mono">{importResult.imported}</span> /
                  Groups: <span className="font-mono">{importResult.total_groups}</span>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="max-h-40 overflow-auto border border-red-200 bg-red-50/40 rounded p-2 space-y-1">
                    {importResult.errors.map((err, i) => (
                      <div key={i} className="text-red-700 font-mono text-[10px]">
                        {err.quotation_number}: {err.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportResult(null); }}>Close</Button>
            <Button variant="outline" onClick={handleExport} data-testid="qtn-export-list-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Export All (Excel)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
